/* eslint-disable jsx-a11y/no-autofocus */
/* eslint-disable react-hooks/exhaustive-deps */
import "./EditResource.scss";
import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import {
  checkFileExists,
  checkFolderExists,
  editFile,
  publishFolder,
  saveFolder,
} from "../../services/folderService";
import {
  FileExistsDto,
  FolderExistsDto,
  PortalFileDto,
  PortalFolderDto,
  PublishDto,
  TypeDto,
} from "../../types/Folder";
import useApi from "../../hooks/useApi";
import { RoleDto, RoleFolderPermissionDto } from "../../types/Role";
import { ResponseDto } from "../../types/Helper";
import { FileActions, FolderFileType, NotificationType } from "../../data/Enums";
import { SEVERITIES, GLOBALSETTINGS } from "../../data/constants";
import { useUser } from "../../context/UserContext";
import { getFileDetails, getFolderDetails } from "../../services/editResourceService";
import PermissionGrid from "../upload/PermissionGrid";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { useToaster } from "../../hooks/useToaster";
import { useDispatch } from "react-redux";
import { setResourceModified } from "../../redux/resourceModifiedSlice";
import { getAllRolesByUser } from "../../services/roleService";
import {
  getDestinationUrl,
  getUncheckedUsers,
  getUserPermissions,
  mapDefaultPermissions,
  mapRolesToPermissions,
  shouldShowMessage,
} from "../../utils/commonHelper";
import { GetAllFilesAndFoldersByUserDto } from "../../types/FolderPermission";
import CloseIcon from "@mui/icons-material/Close";
import { logAuditTrace, removeRecentViewedItem } from "../../services/auditNavigationTraceService";
import BackdropProgressBar from "../../components/ui/BackdropProgressBar";
import isEqual from "lodash/isEqual";
import {
  DESCRIPTION_LENGTH,
  EDITRESOURCEMESSAGES,
  FILE_PUBLISHED_SUCCESS,
  FOLDER_PUBLISHED_SUCCESS,
  FOLDERTREEMESSAGES,
  GLOBALMESSAGES,
  UPDATE_DETAILS,
  UPLOADMESSAGES,
  UPLOADVALIDATIONS,
} from "../../constants/messages";
import { setResourceDeleted } from "../../redux/resourceDeletedSlice";
import { getCurrentESTTime } from "../../utils/dateHelper";

interface EditResourceProps {
  typeId: number;
  type: string;
  isPublish: boolean;
  onClose: () => void;
  canManagePermissions: boolean;
}

const EditResource: FC<EditResourceProps> = ({
  typeId,
  type,
  isPublish,
  onClose,
  canManagePermissions,
}) => {
  const { loading, executeApi } = useApi();
  const showToaster = useToaster();
  const dispatch = useDispatch();
  const { userId, userName } = useUser();
  const [defaultPermission, setDefaultPermission] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [currentRow, setCurrentRow] = useState<PortalFolderDto | PortalFileDto | null>(null);
  const [originalData, setOriginalData] = useState<PortalFolderDto | PortalFileDto | null>(null);
  const [showNotification, setShowNotification] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState<NotificationType[]>([]);
  const [isValidNotificationType, setIsValidNotificationType] = useState(true);
  const inputRef = useRef<HTMLInputElement>(null);
  const hasFocused = useRef(false);

  const itemDetails = (roles: RoleDto[], itemDetails: PortalFolderDto | PortalFileDto) => {
    itemDetails.roleFolderPermissions = itemDetails.roleFolderPermissions.map((permission) => {
      // Find the matching role from `roles`
      const matchingRole = roles.find((role) => role.roleId === permission.roleId);
      return {
        ...permission,
        users: matchingRole
          ? matchingRole.users.map((user) => ({
              userId: user.userId,
              userName: user.userName,
              restrictedUserFolderPermissionsId:
                itemDetails.restrictedUsers.find(
                  (restrictedUser) =>
                    restrictedUser.userId === user.userId &&
                    restrictedUser.roleId === permission.roleId
                )?.restrictedUserFolderPermissionsId ?? null,
              isChecked: !itemDetails.restrictedUsers.some(
                (restrictedUser) =>
                  restrictedUser.userId === user.userId &&
                  restrictedUser.roleId === permission.roleId
              ), // Uncheck if present in restrictedUsers
            }))
          : [], // Default empty array if no matching role
      };
    });
  };

  useEffect(() => {
    const fetchTypeDetails = async () => {
      const typeDto: TypeDto = {
        userId: userId,
        typeId: typeId,
      };

      const roles = await executeApi<number, RoleDto[]>(getAllRolesByUser, typeDto.userId);
      if (type === FolderFileType[FolderFileType.Folder]) {
        const folderDetails = await executeApi<TypeDto, PortalFolderDto>(getFolderDetails, typeDto);
        if (folderDetails !== null) {
          if (roles !== null) {
            folderDetails.roleFolderPermissions = folderDetails.roleFolderPermissions || [];
            folderDetails.restrictedUsers = folderDetails.restrictedUsers || [];

            itemDetails(roles, folderDetails);

            const filteredRoles = roles.filter(
              (role) =>
                !folderDetails.roleFolderPermissions.some(
                  (permission) => permission.roleId === role.roleId
                )
            );

            if (filteredRoles.length > 0) {
              const newRoles = mapRolesToPermissions(filteredRoles, userId, true);
              folderDetails.roleFolderPermissions = [
                ...folderDetails.roleFolderPermissions,
                ...newRoles,
              ];
            }
          }

          setCurrentRow(folderDetails);
          setOriginalData(folderDetails);
        }
      } else {
        const fileDetails = await executeApi<TypeDto, PortalFileDto>(getFileDetails, typeDto);
        if (fileDetails !== null) {
          if (roles !== null) {
            itemDetails(roles, fileDetails);
            const filteredRoles = roles.filter(
              (role) =>
                !fileDetails.roleFolderPermissions.some(
                  (permission) => permission.roleId === role.roleId
                )
            );

            if (filteredRoles.length > 0) {
              const newRoles = mapRolesToPermissions(filteredRoles, userId, true);
              fileDetails.roleFolderPermissions = [
                ...fileDetails.roleFolderPermissions,
                ...newRoles,
              ];
            }
          }

          setCurrentRow(fileDetails);
          setOriginalData(fileDetails);
          setShowNotification(true);
        }
      }
    };
    fetchTypeDetails();
  }, [typeId, type, userId]);

  useEffect(() => {
    if (!hasFocused.current && currentRow && inputRef.current) {
      inputRef.current.focus();
      hasFocused.current = true;
    }
  }, [currentRow]);

  const getUncheckedToCheckedIds = (details: PortalFolderDto | PortalFileDto) => {
    if (!details || !details.roleFolderPermissions) return "";

    // Convert restrictedUsers to a Map for quick lookup
    const restrictedUserMap = new Map(
      details.restrictedUsers?.map((ru) => [`${ru.roleId}-${ru.userId}`, ru]) || []
    );

    const checkedUserIds = details.roleFolderPermissions
      .flatMap(
        (permission) =>
          permission.users
            .filter(
              (user) =>
                user.isChecked && // User is now checked
                restrictedUserMap.has(`${permission.roleId}-${user.userId}`) // Was previously restricted
            )
            .map((user) => user.restrictedUserFolderPermissionsId) // Extract ID
      )
      .filter((id) => id !== null);
    // Remove checked users from folderDetails.restrictedUsers
    details.restrictedUsers =
      details.restrictedUsers?.filter(
        (ru) => !checkedUserIds.includes(ru.restrictedUserFolderPermissionsId)
      ) || [];
    return checkedUserIds.length > 0 ? checkedUserIds.join(",") : "";
  };

  const validatePermissions = useCallback(() => {
    const atLeastOnePermissionChecked = currentRow?.roleFolderPermissions?.some(
      (permission) =>
        permission.canView ||
        permission.canDownload ||
        permission.canShareToUser ||
        permission.canShareToRole ||
        permission.canEdit ||
        permission.canDelete ||
        permission.canManagePermissions
    );

    if (atLeastOnePermissionChecked) {
      setValidationError(null);
      return true;
    } else {
      setValidationError(UPLOADVALIDATIONS.PERMISSION_VALIDATION);
      return false;
    }
  }, [currentRow?.roleFolderPermissions, setValidationError]);

  const handleFolderExists = async (): Promise<boolean> => {
    const parentFolderId =
      type === FolderFileType[FolderFileType.Folder]
        ? ((currentRow as PortalFolderDto).parentFolderId ?? null)
        : ((currentRow as PortalFileDto).folderId ?? null);

    const folderData: FolderExistsDto = {
      description: currentRow.description?.trim(),
      folderId: (currentRow as PortalFolderDto).folderId,
      parentFolderId: parentFolderId,
      createdBy: userId,
    };

    const result = await executeApi<FolderExistsDto, boolean>(checkFolderExists, folderData);

    if (result) {
      setValidationError(UPLOADVALIDATIONS.FOLDER_ALREADY_EXISTS);
    } else {
      setValidationError(null);
    }
    return result;
  };

  const handleFileExists = async (): Promise<boolean> => {
    const parentFolderId =
      type === FolderFileType[FolderFileType.Folder]
        ? ((currentRow as PortalFolderDto).parentFolderId ?? null)
        : ((currentRow as PortalFileDto).folderId ?? null);

    const fileData: FileExistsDto = {
      fileName: currentRow.description?.trim(),
      fileId: (currentRow as PortalFileDto).fileId,
      parentFolderId: parentFolderId,
      createdBy: userId,
    };

    const result = await executeApi<FileExistsDto, boolean>(checkFileExists, fileData);

    if (result) {
      setValidationError(UPLOADVALIDATIONS.FILE_ALREADY_EXISTS);
    } else {
      setValidationError(null);
    }
    return result;
  };

  const validateForm = async () => {
    if (type === FolderFileType[FolderFileType.File]) {
      const fileParts = currentRow.description.split(".");
      const fileName = fileParts.slice(0, -1).join(".").trim();
      if (!fileName) {
        setValidationError(UPLOADVALIDATIONS.FILEEMPTY_VALIDATION);
        return false;
      }
    } else if (!currentRow?.description || !currentRow.description.trim()) {
      setValidationError(UPLOADVALIDATIONS.FOLDEREMPTY_VALIDATION);
      return false;
    }
    if (GLOBALSETTINGS.INVALID_FILENAME_CHARS_REGEXP.test(currentRow.description?.trim())) {
      setValidationError(
        UPLOADVALIDATIONS.FOLDER_SPEC_CHAR_VALIDATION.replace(
          "{Invalid Chars}",
          GLOBALSETTINGS.INVALID_FILENAME_CHARS_TEXT
        )
      );
      return false;
    }

    if (
      currentRow?.description &&
      currentRow?.description?.length > GLOBALSETTINGS.DESCRIPTION_LENGTH
    ) {
      setValidationError(DESCRIPTION_LENGTH(type, GLOBALSETTINGS.DESCRIPTION_LENGTH));
      return false;
    }

    let alreadyExists = false;
    if (type === FolderFileType[FolderFileType.Folder]) {
      alreadyExists = await handleFolderExists();
    } else if (type === FolderFileType[FolderFileType.File]) {
      alreadyExists = await handleFileExists();
    }
    if (alreadyExists) {
      return false;
    }

    const isValid = validatePermissions() && isValidNotificationType;
    return isValid;
  };

  const getFilteredPermissions = () => {
    const hasAnyPermissionOrExistingId = (item: RoleFolderPermissionDto): boolean => {
      const hasPermissions =
        item.canView ||
        item.canDownload ||
        item.canShareToUser ||
        item.canShareToRole ||
        item.canEdit ||
        item.canDelete ||
        item.canManagePermissions;

      return (
        hasPermissions ||
        (item.roleFolderPermissionsId !== null && item.roleFolderPermissionsId > 0)
      );
    };

    return currentRow?.roleFolderPermissions.filter(hasAnyPermissionOrExistingId);
  };

  const handleUpdateFolder = (result: boolean, type: string, errorMessage: string | null) => {
    const folderId =
      type === FolderFileType[FolderFileType.Folder] ? (currentRow?.folderId ?? null) : null;
    const fileId =
      type !== FolderFileType[FolderFileType.Folder]
        ? ((currentRow as PortalFileDto).fileId ?? null)
        : null;

    const userPermissions = getUserPermissions(currentRow, userId);

    if (result) {
      const successMessage =
        type === FolderFileType[FolderFileType.Folder]
          ? isPublish
            ? FOLDER_PUBLISHED_SUCCESS(currentRow?.description)
            : UPDATE_DETAILS(currentRow?.description)
          : isPublish
            ? FILE_PUBLISHED_SUCCESS(currentRow?.description)
            : UPDATE_DETAILS(currentRow?.description);

      const displayMessage = defaultPermission
        ? `${UPLOADMESSAGES.DEFAULT_PERMISSION_TITLE}\n${successMessage}`
        : successMessage;

      showToaster(displayMessage, SEVERITIES.SUCCESS);

      const parentFolderId =
        type === FolderFileType[FolderFileType.Folder]
          ? ((currentRow as PortalFolderDto).parentFolderId ?? null)
          : ((currentRow as PortalFileDto).folderId ?? null);

      const modifiedRow: GetAllFilesAndFoldersByUserDto = {
        roleFolderPermissionsId: 0,
        folderId: folderId ?? -1,
        parentFolderId: parentFolderId,
        fileId: fileId ?? -1,
        description: currentRow?.description ?? "",
        isPublish: currentRow?.isPublish ?? false,
        canView: userPermissions.canView,
        canEdit: userPermissions.canEdit,
        canDelete: userPermissions.canDelete,
        canDownload: userPermissions.canDownload,
        canManagePermissions: userPermissions.canManagePermissions,
        canShareToRole: userPermissions.canShareToRole,
        canShareToUser: userPermissions.canShareToUser,
        totalRecordCount: 0,
      };

      if (userPermissions.canView === false && currentRow?.createdBy !== userId) {
        dispatch(
          setResourceDeleted({
            folderId: type === FolderFileType[FolderFileType.Folder] ? folderId : -1,
            parentFolderId:
              type === FolderFileType[FolderFileType.Folder]
                ? (currentRow as PortalFolderDto).parentFolderId === 0
                  ? null
                  : (currentRow as PortalFolderDto).parentFolderId
                : -1,
            fileId: type === FolderFileType[FolderFileType.File] ? fileId : -1,
          })
        );
      } else {
        dispatch(setResourceModified({ modifiedRow: modifiedRow, type: type }));
      }
    } else {
      showToaster(errorMessage || GLOBALMESSAGES.GLOBAL_ERROR, SEVERITIES.ERROR);
    }

    let actionDescription = FileActions.Edit.toString();
    if (isPublish) {
      actionDescription = FileActions.Publish.toString();
    }
    if (!result) {
      actionDescription += GLOBALMESSAGES.FAILED_ADDITION;
    }
    const auditTrace = {
      userId: userId,
      objectType: type,
      objectId: folderId ?? fileId,
      objectName: currentRow?.description,
      action: actionDescription,
      navigationDateTime: getCurrentESTTime(),
    };
    if (result && userPermissions.canView === false && currentRow?.createdBy !== userId) {
      logAuditTrace(auditTrace, null);
      removeRecentViewedItem(auditTrace, dispatch);
    } else {
      logAuditTrace(auditTrace, dispatch);
    }
  };

  const updateFolder = async () => {
    if (currentRow !== null && (await validateForm())) {
      if (isPublish === false && isEqual(currentRow, originalData)) {
        showToaster(EDITRESOURCEMESSAGES.NO_UPDATE, SEVERITIES.INFO);
        return;
      }
      let defaultPermissions = null;
      const setDefault = shouldShowMessage(currentRow?.roleFolderPermissions, currentRow.createdBy);
      if (setDefault) {
        defaultPermissions = mapDefaultPermissions(
          currentRow?.roleFolderPermissions,
          currentRow.createdBy
        );
        if (defaultPermissions) {
          setCurrentRow({
            ...currentRow,
            roleFolderPermissions: defaultPermissions,
          });
          setDefaultPermission(true);
        }
      } else {
        invokeUpdate();
      }
    }
  };

  useEffect(() => {
    if (defaultPermission) {
      invokeUpdate();
    }
  }, [defaultPermission]);

  const invokeUpdate = async () => {
    let result;
    if (isPublish) {
      currentRow.isPublish = true;
    }
    currentRow.description = currentRow.description?.trim();
    currentRow.removedRestrictedUserIds = getUncheckedToCheckedIds(currentRow);
    currentRow.restrictedUsers = getUncheckedUsers(currentRow.roleFolderPermissions);
    const originalPermissions = currentRow.roleFolderPermissions;
    currentRow.roleFolderPermissions = getFilteredPermissions();
    currentRow.currentUserId = userId;
    if (type === FolderFileType[FolderFileType.Folder]) {
      if (isPublish) {
        const publishData: PublishDto = {
          typeId: typeId,
          userId: userId,
          roleFolderPermissions: currentRow?.roleFolderPermissions,
          restrictedUsers: currentRow?.restrictedUsers,
          removedRestrictedUserIds: currentRow?.removedRestrictedUserIds,
        };
        result = await executeApi<PublishDto, boolean>(publishFolder, publishData);
        handleUpdateFolder(Boolean(result), FolderFileType[FolderFileType.Folder], null);
        if (result) {
          onClose();
        } else {
          currentRow.roleFolderPermissions = originalPermissions;
        }
      } else {
        result = await executeApi<PortalFolderDto, ResponseDto<number>>(
          saveFolder,
          currentRow as PortalFolderDto
        );
        handleUpdateFolder(
          Boolean(result?.isSuccess && result?.data && result?.data > 0),
          FolderFileType[FolderFileType.Folder],
          result?.errorMessage ?? null
        );
      }
      if (result?.isSuccess) {
        onClose();
      } else {
        currentRow.roleFolderPermissions = originalPermissions;
      }
    } else {
      let fileDto = currentRow as PortalFileDto;      
      if (isPublish) {
        const appUrl = getDestinationUrl(currentRow.folderId);
        fileDto.notificationTypes = selectedTypes;
        fileDto.applicationUrl = appUrl;
        fileDto.userName = userName;
        fileDto.isPublishClick = true;
      }
      result = await executeApi<PortalFileDto, ResponseDto<boolean>>(editFile, fileDto);
      handleUpdateFolder(
        Boolean(result?.isSuccess && result?.data),
        FolderFileType[FolderFileType.File],
        result?.errorMessage ?? null
      );
      if (result?.isSuccess) {
        onClose();
      } else {
        currentRow.roleFolderPermissions = originalPermissions;
      }
    }
  };

  /* eslint-disable @typescript-eslint/no-unused-vars */
  const handleNotificationChange = (selected: NotificationType[], notificationEnabled: boolean) => {
    setSelectedTypes(selected);
    if (notificationEnabled && selected.length === 0) {
      setIsValidNotificationType(false);
    } else {
      setIsValidNotificationType(true);
    }
  };
  /* eslint-disable @typescript-eslint/no-unused-vars */

  const handleFileNameChange = (event) => {
    const newFileName = event.target.value;
    const extension = currentRow?.description?.split(".").slice(-1)[0] || "";
    setCurrentRow({ ...currentRow, description: `${newFileName}.${extension}` });
  };

  const handleDescriptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newDescription = event.target.value;
    setCurrentRow((prevRow) => (prevRow ? { ...prevRow, description: newDescription } : null));

    if (newDescription.trim() !== "") {
      setValidationError(null);
    }
  };

  const handleUserChange = (roleId: number, selectedUserIds: number[]) => {
    if (!currentRow) return;

    // Update `roleFolderPermissions` in `currentRow`
    const updatedPermissions = currentRow.roleFolderPermissions.map((role) =>
      role.roleId === roleId
        ? {
            ...role,
            users: role.users.map((user) => ({
              ...user,
              isChecked: selectedUserIds.includes(user.userId), // Set isChecked based on selection
            })),
          }
        : role
    );

    // Update `currentRow` state
    setCurrentRow({
      ...currentRow,
      roleFolderPermissions: updatedPermissions,
    });
  };

  const handlePermissionChange = (
    roleId: number,
    field: keyof RoleFolderPermissionDto,
    value: boolean
  ) => {
    if (!currentRow) return;

    setCurrentRow({
      ...currentRow,
      roleFolderPermissions: currentRow.roleFolderPermissions.map((role) =>
        role.roleId === roleId ? { ...role, [field]: value } : role
      ),
    });
  };

  return (
    <Dialog open fullWidth maxWidth="md">
      <DialogTitle id="customized-dialog-title">
        {isPublish ? EDITRESOURCEMESSAGES.PUBLISH_FOLDER : EDITRESOURCEMESSAGES.EDIT_FOLDER}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={() => ({
            position: "absolute",
            right: 8,
            top: 6,
            color: "#fff",
          })}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {<BackdropProgressBar loading={loading} />}
        {validationError && (
          <Alert severity="error" sx={{ mb: "5px" }}>
            {validationError}
          </Alert>
        )}
        <Box component="form">
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="subtitle1" sx={{ width: "100px", mb: "5px" }}>
              {type === FolderFileType[FolderFileType.Folder] ? "Folder Id" : "File Id"}
            </Typography>
            <Typography variant="subtitle1" sx={{ width: "20px", textAlign: "left", mb: "5px" }}>
              :
            </Typography>
            <Typography variant="subtitle1" sx={{ mb: "5px" }}>
              {typeId}
            </Typography>
          </Box>

          <Box display="flex" gap={1} mt={2}>
            <Typography variant="subtitle1" sx={{ width: "100px", mb: "5px" }}>
              Name
            </Typography>
            <Typography variant="subtitle1" sx={{ width: "20px", textAlign: "left", mb: "5px" }}>
              :
            </Typography>
            {isPublish ? (
              <Typography variant="subtitle1" sx={{ mb: "5px" }}>
                {currentRow?.description || ""}
              </Typography>
            ) : type === FolderFileType[FolderFileType.File] ? (
              <div className="file-flex-container">
                <TextField
                  id="FileName"
                  label="File Name"
                  variant="outlined"
                  size="small"
                  inputRef={inputRef}
                  inputProps={{ autoComplete: "new-password" }}
                  value={currentRow?.description?.split(".").slice(0, -1).join(".") || ""}
                  onChange={handleFileNameChange}
                  style={{ flexGrow: 1 }}
                  sx={{ mb: "5px" }}
                />
                <TextField
                  label="Extension"
                  variant="outlined"
                  size="small"
                  disabled
                  value={`.${currentRow?.description?.split(".").slice(-1)[0] || ""}`}
                  className="ext-input"
                  sx={{ mb: "5px" }}
                />
              </div>
            ) : (
              <div className="file-flex-container">
                <TextField
                  id="FolderName"
                  label="Name"
                  variant="outlined"
                  size="small"
                  inputRef={inputRef}
                  inputProps={{ autoComplete: "new-password" }}
                  value={currentRow?.description || ""}
                  onChange={handleDescriptionChange}
                  style={{ flexGrow: 1 }}
                  sx={{ mb: "5px" }}
                />
              </div>
            )}
          </Box>

          {currentRow?.parentFolderName ? (
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="subtitle1" sx={{ width: "100px", mb: "5px" }}>
                Parent Folder
              </Typography>
              <Typography variant="subtitle1" sx={{ width: "20px", textAlign: "left", mb: "5px" }}>
                :
              </Typography>
              <Typography variant="subtitle1" sx={{ mb: "5px" }}>
                {currentRow?.parentFolderName ?? FOLDERTREEMESSAGES.ROOT_FOLDER}
              </Typography>
            </Box>
          ) : (
            ""
          )}
          <Box mt={2}>
            <PermissionGrid
              permissions={currentRow?.roleFolderPermissions ?? []}
              onPermissionChange={handlePermissionChange}
              onUserChange={handleUserChange}
              columnVisibilityModel={{ roleId: false }}
              disableAll={!canManagePermissions}
            />
          </Box>
          {showNotification && isPublish
            ? /*Bug 57008 - Intentionally commenting for now. */
              // <Box mt={0.5}>
              //   <NotificationSettings onChange={handleNotificationChange} />
              // </Box>
              null
            : null}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button color="light" variant="contained" onClick={onClose}>
          {EDITRESOURCEMESSAGES.CANCEL}
        </Button>
        <Button id="Update" autoFocus variant="contained" onClick={updateFolder}>
          {isPublish ? EDITRESOURCEMESSAGES.PUBLISH : EDITRESOURCEMESSAGES.UPDATE}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditResource;
